import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  listBoards,
  getBoard,
  createBoard,
  updateBoard,
  deleteBoard,
  listBoardWidgets,
  createWidget,
  updateWidget,
  deleteWidget,
  resyncWidget,
  listBoardAccess,
  addBoardAccess,
  removeBoardAccess,
} from "@/lib/http/board"
import type { AddBoardAccessPayload, RemoveBoardAccessPayload } from "@/lib/http/board"

export const useListBoards = () =>
  useQuery({
    queryKey: ["boards"],
    queryFn: listBoards,
  })

export const useGetBoard = (id: string | null) =>
  useQuery({
    queryKey: ["boards", id],
    queryFn: () => getBoard(id!),
    enabled: !!id,
  })

export const useCreateBoard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBoard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
    },
  })
}

export const useUpdateBoard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; description?: string; config?: Record<string, any> }) =>
      updateBoard(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
      queryClient.invalidateQueries({ queryKey: ["boards", variables.id] })
    },
  })
}

export const useDeleteBoard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteBoard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
    },
  })
}

export const useListBoardWidgets = (boardId: string | null) =>
  useQuery({
    queryKey: ["boards", boardId, "widgets"],
    queryFn: () => listBoardWidgets(boardId!),
    enabled: !!boardId,
  })

export const useCreateWidget = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      boardId,
      ...data
    }: {
      boardId: string
      name: string
      widget_type: "table" | "chart" | "number" | "text"
      sql_query: string
      resource_id: string
      config?: Record<string, any>
      position?: number
    }) => createWidget(boardId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "widgets"] })
    },
  })
}

export const useUpdateWidget = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      boardId,
      widgetId,
      ...data
    }: {
      boardId: string
      widgetId: string
      name?: string
      widget_type?: "table" | "chart" | "number" | "text"
      sql_query?: string
      resource_id?: string
      config?: Record<string, any>
      position?: number
    }) => updateWidget(boardId, widgetId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "widgets"] })
    },
  })
}

export const useDeleteWidget = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, widgetId }: { boardId: string; widgetId: string }) => deleteWidget(boardId, widgetId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "widgets"] })
    },
  })
}

export const useWidgetData = (boardId: string | null, widgetId: string | null) =>
  useQuery({
    queryKey: ["boards", boardId, "widgets", widgetId, "data"],
    queryFn: () => resyncWidget(boardId!, widgetId!),
    enabled: !!boardId && !!widgetId,
    staleTime: Infinity, // Data never goes stale automatically
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  })

export const useResyncWidget = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, widgetId }: { boardId: string; widgetId: string }) => resyncWidget(boardId, widgetId),
    onSuccess: (data, variables) => {
      // Update the widget data cache
      queryClient.setQueryData(["boards", variables.boardId, "widgets", variables.widgetId, "data"], data)
      // Also invalidate widgets list to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "widgets"] })
    },
  })
}

// Board access (members + role grants)

export const useListBoardAccess = (boardId: string | null) =>
  useQuery({
    queryKey: ["boards", boardId, "access"],
    queryFn: () => listBoardAccess(boardId!),
    enabled: !!boardId,
  })

export const useAddBoardAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, payload }: { boardId: string; payload: AddBoardAccessPayload }) =>
      addBoardAccess(boardId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "access"] })
    },
  })
}

export const useRemoveBoardAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, payload }: { boardId: string; payload: RemoveBoardAccessPayload }) =>
      removeBoardAccess(boardId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["boards", variables.boardId, "access"] })
    },
  })
}
